import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { authClient } from '@/lib/auth-client'
import {
  AuthErrorAlert,
  AuthInput,
  AuthLayout,
  AuthSubmitButton,
} from '@/features/auth/components'
import { Button } from '@/components/ui/button'
import { generateLinks, generateMetaTags, routeSEO } from '@/lib/seo'

export const Route = createFileRoute('/forgot-password')({
  head: () => ({
    meta: generateMetaTags(routeSEO['/forgot-password']),
    links: generateLinks(routeSEO['/forgot-password']),
  }),
  component: ForgotPasswordPage,
})

const ENDPOINT = '/api/auth/request-password-reset'

function ForgotPasswordPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')

  const searchParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    const formData = new FormData(e.currentTarget)
    const formEmail = formData.get('email') as string

    try {
      const { error: authError } = await authClient.requestPasswordReset({
        email: formEmail,
        redirectTo: '/reset-password',
      })

      if (authError) {
        setError(authError.message || 'Failed to send reset email')
        return
      }

      setEmail(formEmail)
      setIsSuccess(true)
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <AuthLayout
        endpoint={ENDPOINT}
        title="Check your email"
        description={
          <>
            We've sent a password reset link to{' '}
            <span className="font-mono text-[var(--color-text-primary)]">
              {email}
            </span>
          </>
        }
      >
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-[var(--color-info-border)] bg-[var(--color-info-bg)] px-4 py-3">
          <span className="material-symbols-outlined mt-px shrink-0 text-lg text-[var(--color-info)]">
            info
          </span>
          <p className="text-body-sm text-[var(--color-info-text)]">
            Didn't receive the email? Check your spam folder, or{' '}
            <button
              type="button"
              onClick={() => setIsSuccess(false)}
              className="rounded-full font-semibold underline-offset-4 transition-colors duration-300 ease-brisk hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/50"
            >
              try again
            </button>
          </p>
        </div>

        <Button asChild variant="secondary" className="w-full font-medium">
          <Link
            to="/sign-in"
            search={searchParams ? Object.fromEntries(searchParams) : {}}
          >
            <span className="material-symbols-outlined text-lg">
              arrow_back
            </span>
            Back to sign in
          </Link>
        </Button>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      endpoint={ENDPOINT}
      title="Forgot your password?"
      description="Enter your email address and we'll send you a link to reset your password."
      footer={
        <Link
          to="/sign-in"
          className="inline-flex items-center gap-1.5 transition-colors hover:text-[var(--color-text-primary)]"
        >
          <span className="material-symbols-outlined text-base">
            arrow_back
          </span>
          Back to sign in
        </Link>
      }
    >
      <AuthErrorAlert error={error} />

      <form onSubmit={handleSubmit} className="space-y-5">
        <AuthInput
          type="email"
          id="email"
          name="email"
          label="Email address"
          placeholder="you@company.com"
          icon="mail"
          required
          disabled={isLoading}
        />

        <AuthSubmitButton isLoading={isLoading} icon="send">
          Send reset link
        </AuthSubmitButton>
      </form>
    </AuthLayout>
  )
}
